import { useFormik } from "formik";
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from "react";
import { useLeadStore, useLeaseCompStore, usePipedriveStore, useSaleCompStore, useTenantInMarketStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useDealsInsideShape from "../../hooks/useDealsInsideShape";
import useDealsWithGlobalFilters from "../../hooks/useDealsWithGlobalFilters";
import useInsightsShape from "../../hooks/useInsightsShape";
import useLeadsInsideShape from "../../hooks/useLeadsInsideShape";
import useLeadsWithGlobalFilters from "../../hooks/useLeadsWithGlobalFilters";
import useLeaseCompsInsideShape from "../../hooks/useLeaseCompsInsideShape";
import { useRecordedSalesInsideShape } from "../../hooks/useRecordedSales";
import useSaleCompsInsideShape from "../../hooks/useSaleCompsInsideShape";
import useTenantsInMarketInsideShape from "../../hooks/useTenantsInMarketInsideShape";
import useValidateInsightsExport from "../../hooks/useValidateInsightsExport";
import ExportApiService from "../../services/exportApiService";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import EditControls from "../EditControls";
import CheckboxInput from "../inputs/CheckboxInput";
import StringInput from "../inputs/StringInput";
import Modal from "../Modal";
import { showErrorToast, showSuccessToast } from "../ToastContainer";
import styles from "./InsightsExportData.module.css";

function InsightsExportData({ onClose }: IInsightsExportDataProps) {
    const validateInsightsExport = useValidateInsightsExport();
    const exportApiService = useMemo(() => new ExportApiService(), []);
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const tenantsInMarketStore = useTenantInMarketStore();
    const insightsShape = useInsightsShape();
    const deals = useDealsWithGlobalFilters(useDealsInsideShape(pipedriveStore.deals, insightsShape));
    const leads = useLeadsWithGlobalFilters(useLeadsInsideShape(leadStore.leads, insightsShape));
    const leaseComps = useLeaseCompsInsideShape(leaseCompStore.leaseComps, insightsShape);
    const saleComps = useSaleCompsInsideShape(saleCompStore.saleComps, insightsShape);
    const recordedSales = useRecordedSalesInsideShape(insightsShape);
    const tenantsInMarket = useTenantsInMarketInsideShape(tenantsInMarketStore.tenantsInMarket, insightsShape);
    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik<IInsightsExportState>({
        initialValues: InsightsExportState.empty(),
        validate: validateInsightsExport,
        onSubmit: async (values) => {
            setIsLoading(true);
            try {
                await exportApiService.export({
                    email: values.email!,
                    dealIds: values.areDealsSelected ? deals.map(d => d.id) : [],
                    leadIds: values.areLeadsSelected ? leads.map(l => l.id) : [],
                    leaseCompIds: values.areLeaseCompsSelected ? leaseComps.map(lc => lc.id) : [],
                    saleCompIds: values.areSaleCompsSelected ? saleComps.map(sc => sc.id) : [],
                    recordedSaleIds: values.areRecordedSalesSelected ? recordedSales.map(rs => rs.id) : [],
                    tenantInMarketIds: values.areTenantsInMarketSelected ? tenantsInMarket.map(tim => tim.id) : [],
                });

                showSuccessToast();
                onClose();
            } catch (e) {
                showErrorToast();
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
    });

    useClosable(ClosableOrder.High, onClose);

    return (
        <Modal
            header="Export Data"
            onClose={onClose}
        >
            Data will be sent to the following email address:
            <StringInput formik={formik} label="" name="email" placeholder="Email" containerClassName={styles.email} />
            <CheckboxInput formik={formik} label="Deals" name="areDealsSelected" className={styles.checkbox} />
            <CheckboxInput formik={formik} label="Leads" name="areLeadsSelected" className={styles.checkbox} />
            <CheckboxInput formik={formik} label="Lease Comps" name="areLeaseCompsSelected" className={styles.checkbox} />
            <CheckboxInput formik={formik} label="Sale Comps" name="areSaleCompsSelected" className={styles.checkbox} />
            <CheckboxInput formik={formik} label="Recorded Sales" name="areRecordedSalesSelected" className={styles.checkbox} />
            <CheckboxInput formik={formik} label="Tenants in Market" name="areTenantsInMarketSelected" className={styles.checkbox} />
            <div className={[commonStyles.modalControls, commonStyles.controlGroup].join(" ")}>
                <EditControls
                    isStateChanged={formik.dirty}
                    isUpdateLoading={isLoading}
                    onUpdate={formik.handleSubmit}
                    onCancel={onClose}
                    primaryLabel="Send"
                />
            </div>
        </Modal>
    );
}

export default observer(InsightsExportData);

interface IInsightsExportDataProps {
    onClose: () => void;
}

export interface IInsightsExportState {
    email: string | null;
    areDealsSelected: boolean;
    areLeadsSelected: boolean;
    areLeaseCompsSelected: boolean;
    areSaleCompsSelected: boolean;
    areRecordedSalesSelected: boolean;
    areTenantsInMarketSelected: boolean;
}

class InsightsExportState implements IInsightsExportState {
    email: string | null = null;
    areDealsSelected: boolean = false;
    areLeadsSelected: boolean = false;
    areLeaseCompsSelected: boolean = false;
    areSaleCompsSelected: boolean = false;
    areRecordedSalesSelected: boolean = false;
    areTenantsInMarketSelected: boolean = false;

    static empty(): InsightsExportState {
        return new InsightsExportState();
    }
}